import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../api/client.js';
import { login as apiLogin, logout as apiLogout, fetchCurrentUser } from '../api/auth.js';
import { impersonateUser } from '../api/admin.js';

const SESSION_FLAG = 'calanoSession';

/**
 * Auth hook — manages the session marker, user, impersonation, login/logout.
 * The session itself is an httpOnly cookie; `token` is only a non-secret marker ('cookie' or null).
 *
 * @param {object} params
 * @param {import('@tanstack/react-query').QueryClient} params.queryClient
 * @param {function} [params.onLogout]        — called after logout to reset local UI state
 * @param {function} [params.onUnauthorized]  — called on 401 to clear selection state
 */
export function useAuth ({ queryClient, onLogout, onUnauthorized }) {
  const [token, setToken] = useState(() => (localStorage.getItem(SESSION_FLAG) === '1' ? 'cookie' : null));
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(false);
  const navigate = useNavigate();

  const impersonatedBy = user?.impersonatedBy || null;
  const isAdmin = user?.role === 'ADMIN';
  const isTeamLead = user?.role === 'TEAM_LEAD';
  const canManageTeam = isAdmin || isTeamLead;

  useEffect(() => {
    const interceptor = apiClient.interceptors.response.use(
      response => response,
      error => {
        if (error?.status === 401 || error?.originalError?.response?.status === 401) {
          setToken(null);
          setUser(null);
          localStorage.removeItem(SESSION_FLAG);
          onUnauthorized?.();
        }
        return Promise.reject(error);
      }
    );
    return () => apiClient.interceptors.response.eject(interceptor);
  }, [onUnauthorized]);

  useEffect(() => {
    if (!token) return;
    fetchCurrentUser()
      .then(u => setUser(u))
      .catch(err => {
        console.error('Failed to fetch auth context', err);
        setToken(null);
        localStorage.removeItem(SESSION_FLAG);
      });
  }, [token]);

  const handleLogin = async (email, password, rememberMe = false) => {
    setAuthLoading(true);
    try {
      const { user: newUser } = await apiLogin(email, password, rememberMe);
      localStorage.setItem(SESSION_FLAG, '1');
      setToken('cookie');
      setUser(newUser);

      queryClient.invalidateQueries();

      if (newUser?.role === 'ACCOUNTANT') {
        navigate('/invoices', { replace: true });
      }
    } catch (err) {
      console.error('Login error:', err);
      throw err;
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogout = useCallback(async () => {
    try {
      await apiLogout();
    } catch (err) {
      console.warn('[auth.logout] server bump failed (best-effort)', err?.message || err);
    }
    localStorage.removeItem(SESSION_FLAG);
    setToken(null);
    setUser(null);
    navigate('/');
    queryClient.clear();
    onLogout?.();
  }, [navigate, queryClient, onLogout]);

  const handleExitImpersonation = useCallback(async () => {
    try {
      await apiLogout();
    } catch (err) {
      console.warn('[auth.logout] impersonation exit failed (best-effort)', err?.message || err);
    }
    localStorage.removeItem(SESSION_FLAG);
    setToken(null);
    setUser(null);
    window.location.href = '/login';
  }, []);

  const handleImpersonate = useCallback(async (userId) => {
    if (!userId || userId === user?.id) return;
    try {
      const result = await impersonateUser(userId);
      if (result.ok) {
        setUser(result.user);
        window.location.reload();
      }
    } catch (err) {
      console.error('Impersonate failed', err);
      alert('Не удалось войти как пользователь');
    }
  }, [user?.id]);

  return {
    token, user, isAdmin, isTeamLead, canManageTeam,
    impersonatedBy, authLoading,
    handleLogin, handleLogout, handleExitImpersonation, handleImpersonate
  };
}
